import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Notice, NoticeAssignment } from '@cloudsign/shared';
import { Plus } from 'lucide-react';
import { api } from '../../lib/api';
import { formatDateTime } from '../../lib/format';
import { t } from '../../lib/i18n';
import { useCan } from '../../lib/session';
import { Alert, Badge, Button, Card, CheckboxField, ConfirmButton, EmptyRow, LinkButton, PageHeader, SelectField, Spinner, TableWrap, TextField, Th } from '../../components/ui';
import { ErrorLine, ErrorPanel, fieldErrors } from '../../components/ErrorPanel';
import { SortableList } from '../../components/SortableList';
import { useToastOnSuccess } from '../../components/Toast';
import { NoticeAssignmentList, NoticeList, NoticeVersionList, useSiteGroups, useSiteRecord } from './api';
import { NoticeTextEditor } from './NoticeTextEditor';
import { NoticeView } from './noticeText';

const NOTICE_MAX_PER_GROUP = 20;

/** /manage/notices */
export function NoticeLibraryPage() {
  const canManage = useCan('notices.manage');
  const [status, setStatus] = useState<'active' | 'archived' | 'all'>('active');
  const q = useQuery({ queryKey: ['notices', 'list', status], queryFn: () => api.get('/notices', { query: { status }, schema: NoticeList }) });
  const rows = q.data ?? [];
  return (
    <div>
      <PageHeader
        title={t('fnUi.notices.title')}
        description={t('fnUi.notices.description')}
        actions={
          canManage ? (
            <LinkButton to="/manage/notices/new" variant="primary">
              <Plus aria-hidden="true" className="h-4 w-4" />
              {t('fnUi.notices.new')}
            </LinkButton>
          ) : null
        }
      />
      <div className="mb-4 max-w-xs">
        <SelectField label={t('common.status')} value={status} onChange={(e) => setStatus(e.target.value as typeof status)}>
          <option value="active">{t('fnUi.notices.filter.active')}</option>
          <option value="archived">{t('fnUi.notices.filter.archived')}</option>
          <option value="all">{t('fnUi.notices.filter.all')}</option>
        </SelectField>
      </div>
      {q.error ? <ErrorPanel error={q.error} onRetry={() => void q.refetch()} /> : null}
      {q.isLoading ? <Spinner /> : null}
      {q.data ? (
        <TableWrap caption={t('fnUi.notices.title')}>
          <thead className="border-b border-slate-200 bg-slate-50">
            <tr>
              <Th>{t('fnUi.notices.col.title')}</Th>
              <Th>{t('fnUi.notices.col.version')}</Th>
              <Th>{t('fnUi.notices.col.updated')}</Th>
              <Th>{t('fnUi.notices.col.usedIn')}</Th>
              <Th>{t('fnUi.notices.col.accept')}</Th>
              <Th>{t('fnUi.notices.col.signature')}</Th>
              <Th>{t('fnUi.notices.col.status')}</Th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <EmptyRow cols={7} />
            ) : (
              rows.map((n) => (
                <tr key={n.id} className="border-b border-slate-100 align-top last:border-0">
                  <td className="px-4 py-2.5">
                    <Link className="font-medium text-brand underline" to={`/manage/notices/${n.id}`}>
                      {n.title}
                    </Link>
                  </td>
                  <td className="px-4 py-2.5">{t('fnUi.notices.versionShort', { version: n.currentVersion })}</td>
                  <td className="px-4 py-2.5">{formatDateTime(n.updatedAt)}</td>
                  <td className="px-4 py-2.5">
                    {n.usedIn.length === 0 ? (
                      <span className="text-slate-600">{t('fnUi.notices.notUsed')}</span>
                    ) : (
                      <ul>
                        {n.usedIn.map((u) => (
                          <li key={`${u.siteId}-${u.groupId}`}>{t('fnUi.notices.usedItem', { site: u.siteName, group: u.groupName })}</li>
                        ))}
                      </ul>
                    )}
                  </td>
                  <td className="px-4 py-2.5">{n.requiresAcceptance ? t('common.yes') : t('common.no')}</td>
                  <td className="px-4 py-2.5">{n.requireSignature ? t('common.yes') : t('common.no')}</td>
                  <td className="px-4 py-2.5">
                    <Badge tone={n.status === 'active' ? 'good' : 'neutral'}>{t(`fnUi.notices.status.${n.status}`)}</Badge>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </TableWrap>
      ) : null}
    </div>
  );
}

function VersionHistory({ noticeId, currentVersion }: { noticeId: string; currentVersion: number }) {
  const [open, setOpen] = useState<number | null>(null);
  const q = useQuery({ queryKey: ['notices', 'versions', noticeId, currentVersion], queryFn: () => api.get(`/notices/${noticeId}/versions`, { schema: NoticeVersionList }) });
  return (
    <Card title={t('fnUi.versions.title')} description={t('fnUi.versions.immutable')}>
      {q.isLoading ? <Spinner /> : null}
      {q.error ? <Alert>{t('fnUi.versions.loadFailed')}</Alert> : null}
      <ul className="space-y-2">
        {(q.data ?? []).map((v) => (
          <li key={v.id} className="rounded-lg border border-slate-200 p-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span className="text-sm">
                {t('fnUi.versions.item', { version: v.version, when: formatDateTime(v.createdAt) })}{' '}
                {v.version === currentVersion ? <Badge tone="good">{t('fnUi.versions.current')}</Badge> : null}
              </span>
              <Button aria-expanded={open === v.version} onClick={() => setOpen(open === v.version ? null : v.version)}>
                {open === v.version ? t('fnUi.versions.hide', { version: v.version }) : t('fnUi.versions.view', { version: v.version })}
              </Button>
            </div>
            {open === v.version ? (
              <div className="mt-3 rounded-md border border-slate-200 bg-slate-50 p-4" data-testid={`version-${v.version}`}>
                <p className="mb-2 font-semibold text-slate-900">{v.title}</p>
                <NoticeView body={v.body} />
              </div>
            ) : null}
          </li>
        ))}
      </ul>
    </Card>
  );
}

/** /manage/notices/new and /manage/notices/:id */
export function NoticeEditorPage() {
  const { id } = useParams();
  const isNew = !id || id === 'new';
  const navigate = useNavigate();
  const qc = useQueryClient();
  const canManage = useCan('notices.manage');
  const canDelete = useCan('notices.delete');
  const existing = useQuery({ queryKey: ['notices', 'detail', id], queryFn: () => api.get(`/notices/${id}`, { schema: Notice }), enabled: !isNew });
  const notice = existing.data;
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [accept, setAccept] = useState(true);
  const [sign, setSign] = useState(false);
  const bodyQuery = useQuery({
    queryKey: ['notices', 'body', id, notice?.currentVersionId],
    queryFn: () => api.get(`/notices/${id}/versions`, { schema: NoticeVersionList }),
    enabled: !isNew && Boolean(notice),
  });
  const currentBody = bodyQuery.data?.find((v) => v.id === notice?.currentVersionId)?.body;
  useEffect(() => {
    if (notice) {
      setTitle(notice.title);
      setAccept(notice.requiresAcceptance);
      setSign(notice.requireSignature);
    }
  }, [notice]);
  useEffect(() => {
    if (currentBody !== undefined) setBody(currentBody);
  }, [currentBody]);

  const refresh = async () => {
    await qc.invalidateQueries({ queryKey: ['notices'] });
  };
  const save = useMutation({
    mutationFn: () => {
      const payload = { title: title.trim(), body, requiresAcceptance: accept, requireSignature: accept && sign };
      return isNew ? api.post('/notices', { body: payload, schema: Notice }) : api.patch(`/notices/${id}`, { body: payload, schema: Notice });
    },
    onSuccess: async (n) => {
      await refresh();
      if (isNew) void navigate(`/manage/notices/${n.id}`, { replace: true });
    },
  });
  const status = useMutation({
    mutationFn: (next: 'active' | 'archived') => api.patch(`/notices/${id}`, { body: { status: next }, schema: Notice }),
    onSuccess: refresh,
  });
  const remove = useMutation({
    mutationFn: () => api.delete(`/notices/${id}`),
    onSuccess: async () => {
      await refresh();
      void navigate('/manage/notices');
    },
  });
  useToastOnSuccess(save.isSuccess, t('common.saved'));
  const errs = fieldErrors(save.error);
  const submit = (e: FormEvent) => {
    e.preventDefault();
    save.mutate();
  };

  if (!isNew && existing.error) return <ErrorPanel error={existing.error} onRetry={() => void existing.refetch()} />;
  if (!isNew && !notice) return <Spinner />;
  const readOnly = !canManage;
  return (
    <div className="space-y-6">
      <p className="text-sm">
        <Link to="/manage/notices" className="text-brand underline">
          {t('fnUi.editor.back')}
        </Link>
      </p>
      <PageHeader title={isNew ? t('fnUi.editor.newTitle') : (notice?.title ?? '')} />
      <form onSubmit={submit} noValidate className="space-y-4">
        <Card>
          <div className="space-y-4">
            <TextField label={t('fnUi.editor.titleField')} value={title} maxLength={120} required disabled={readOnly} error={errs['title']} onChange={(e) => setTitle(e.target.value)} />
            <NoticeTextEditor label={t('fnUi.editor.bodyField')} value={body} onChange={setBody} disabled={readOnly} error={errs['body']} />
            <CheckboxField label={t('fnUi.editor.acceptance')} hint={t('fnUi.editor.acceptanceHint')} checked={accept} disabled={readOnly} onChange={(e) => setAccept(e.target.checked)} />
            <CheckboxField label={t('fnUi.editor.signature')} hint={t('fnUi.editor.signatureHint')} checked={accept && sign} disabled={readOnly || !accept} onChange={(e) => setSign(e.target.checked)} />
            {!isNew ? <p className="text-xs text-slate-600">{t('fnUi.editor.newVersionNote')}</p> : null}
            <ErrorLine error={save.error} />
          </div>
        </Card>
        {canManage ? (
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex flex-wrap items-center gap-2">
              {!isNew && notice ? (
                <Button disabled={status.isPending} onClick={() => status.mutate(notice.status === 'active' ? 'archived' : 'active')}>
                  {notice.status === 'active' ? t('fnUi.editor.archive') : t('fnUi.editor.restore')}
                </Button>
              ) : null}
              {!isNew && canDelete ? (
                <ConfirmButton label={t('fnUi.editor.delete')} confirmLabel={t('fnUi.editor.deleteConfirm')} pending={remove.isPending} onConfirm={() => remove.mutate()} />
              ) : null}
            </div>
            <Button type="submit" variant="primary" disabled={save.isPending || !title.trim()}>
              {isNew ? t('fnUi.editor.create') : t('fnUi.editor.save')}
            </Button>
          </div>
        ) : null}
        <ErrorLine error={status.error ?? remove.error} />
        {!isNew && canDelete ? <p className="text-xs text-slate-600">{t('fnUi.editor.deleteHint')}</p> : null}
      </form>
      {!isNew && notice ? <VersionHistory noticeId={notice.id} currentVersion={notice.currentVersion} /> : null}
    </div>
  );
}

/** /manage/sites/:id/notices */
export function SiteNoticesPage() {
  const { id = '' } = useParams();
  const canManage = useCan('notices.manage');
  const qc = useQueryClient();
  const site = useSiteRecord(id);
  const groups = useSiteGroups(id);
  const library = useQuery({ queryKey: ['notices', 'list', 'active'], queryFn: () => api.get('/notices', { query: { status: 'active' }, schema: NoticeList }) });
  const assignments = useQuery({
    queryKey: ['notices', 'assignments', id],
    queryFn: () => api.get('/notices/assignments', { query: { siteId: id }, schema: NoticeAssignmentList }),
  });
  const [picks, setPicks] = useState<Record<string, string>>({});

  const put = useMutation({
    mutationFn: (v: { groupId: string; noticeIds: string[] }) =>
      api.put('/notices/assignments', { body: { siteId: id, groupId: v.groupId, noticeIds: v.noticeIds }, schema: NoticeAssignmentList }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['notices'] });
    },
  });
  useToastOnSuccess(put.isSuccess, t('common.saved'));

  const name = site.data?.name ?? '';
  const err = site.error ?? groups.error ?? library.error ?? assignments.error;
  if (err) return <ErrorPanel error={err} />;
  const byId = new Map((library.data ?? []).map((n) => [n.id, n]));
  const orderedFor = (groupId: string) =>
    (assignments.data ?? [])
      .filter((a: NoticeAssignment) => a.groupId === groupId)
      .sort((a, b) => a.sortOrder - b.sortOrder)
      .map((a) => a.noticeId);

  return (
    <div>
      <p className="mb-2 text-sm">
        <Link to={`/manage/sites/${id}`} className="text-brand underline">
          {t('fnUi.assign.back', { site: name })}
        </Link>
      </p>
      <PageHeader title={t('fnUi.assign.title', { site: name })} description={t('fnUi.assign.description')} />
      {site.isLoading || groups.isLoading || library.isLoading || assignments.isLoading ? <Spinner /> : null}
      {groups.data && groups.data.length === 0 ? <p className="text-sm text-slate-600">{t('fnUi.assign.noGroups')}</p> : null}
      <div className="space-y-6">
        {(groups.data ?? []).map((g) => {
          const ids = orderedFor(g.id);
          const items = ids.map((nid) => byId.get(nid)).filter((n): n is Notice => Boolean(n));
          const addable = (library.data ?? []).filter((n) => !ids.includes(n.id));
          const pick = picks[g.id] ?? '';
          const commit = (next: string[]) => put.mutate({ groupId: g.id, noticeIds: next });
          return (
            <Card key={g.id} title={t('fnUi.assign.group', { group: g.name })}>
              {items.length === 0 ? <p className="mb-3 text-sm text-slate-600">{t('fnUi.assign.empty')}</p> : null}
              {items.length ? (
                <SortableList
                  items={items}
                  getKey={(n) => n.id}
                  getLabel={(n) => n.title}
                  label={t('fnUi.assign.listLabel', { group: g.name })}
                  disabled={!canManage || put.isPending}
                  onChange={(next) => commit(next.map((n) => n.id))}
                  renderItem={(n) => (
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <span className="text-sm font-medium text-slate-900">
                        {n.title} <span className="font-normal text-slate-600">{t('fnUi.notices.versionShort', { version: n.currentVersion })}</span>
                      </span>
                      {canManage ? (
                        <Button aria-label={t('fnUi.assign.remove', { name: n.title, group: g.name })} onClick={() => commit(ids.filter((x) => x !== n.id))}>
                          {t('common.delete')}
                        </Button>
                      ) : null}
                    </div>
                  )}
                />
              ) : null}
              {canManage ? (
                <div className="mt-4 flex flex-wrap items-end gap-2">
                  <SelectField
                    className="min-w-[14rem] flex-1"
                    label={t('fnUi.assign.pick', { group: g.name })}
                    value={pick}
                    disabled={addable.length === 0 || ids.length >= NOTICE_MAX_PER_GROUP}
                    hint={ids.length >= NOTICE_MAX_PER_GROUP ? t('fnUi.assign.max', { max: NOTICE_MAX_PER_GROUP }) : addable.length === 0 ? t('fnUi.assign.noneToAdd') : undefined}
                    onChange={(e) => setPicks({ ...picks, [g.id]: e.target.value })}
                  >
                    <option value="">{t('fnUi.assign.choose')}</option>
                    {addable.map((n) => (
                      <option key={n.id} value={n.id}>
                        {n.title}
                      </option>
                    ))}
                  </SelectField>
                  <Button
                    variant="primary"
                    aria-label={t('fnUi.assign.addTo', { group: g.name })}
                    disabled={!pick || put.isPending}
                    onClick={() => {
                      commit([...ids, pick]);
                      setPicks({ ...picks, [g.id]: '' });
                    }}
                  >
                    {t('fnUi.assign.addButton')}
                  </Button>
                </div>
              ) : null}
            </Card>
          );
        })}
      </div>
      <ErrorLine error={put.error} />
    </div>
  );
}
